/**
 * How long ago something happened, in words and in a colour band. Pure (no
 * React, no MUI), so any slice can use it: the Users index "Last login", the
 * Licences index activity columns, the licence detail KPI band and activity feed.
 *
 * Days are **calendar** days in the browser's local time, not elapsed 24-hour
 * periods: `Today` means "earlier today", `Yesterday` "the day before today",
 * whatever the hour. An elapsed-period count would call last night's 23:10
 * "Today" at 09:00, and print it that way next to a clock time. This is the
 * one place the rule lives, so the same timestamp reads the same on every screen.
 */

const DAY_MS = 86_400_000;

/** Below this many days, an event counts as recent (the `info` band). */
export const RECENT_DAYS = 7;

/** A dated event's colour band: theme palette keys, so a view can paint them directly. */
export type RecencyTone = 'success' | 'info' | 'warning';

/** Local midnight of `d`, in epoch milliseconds. */
const startOfLocalDay = (d: Date) => new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();

/**
 * Calendar days between `iso` and `now`, in local time: 0 the same day, 1 the day
 * before, however few hours apart. Rounded, so a daylight-saving night (23 or 25
 * hours) still counts as one day. A clock-skewed future timestamp reads as 0
 * rather than as a negative count.
 */
export function daysSince(iso: string, now: Date = new Date()): number {
  return Math.max(0, Math.round((startOfLocalDay(now) - startOfLocalDay(new Date(iso))) / DAY_MS));
}

const plural = (n: number, unit: string) => `${n} ${unit}${n > 1 ? 's' : ''} ago`;

/**
 * `Today` · `Yesterday` · `N days ago` · `N weeks ago` · `N months ago` · `N years ago`.
 *
 * The scale coarsens as it goes back: past a month, the difference between the
 * 12th and the 19th stops mattering and the number of months is the fact.
 */
export function relativeDaysLabel(days: number): string {
  if (days === 0) return 'Today';
  if (days === 1) return 'Yesterday';
  if (days < 7) return plural(days, 'day');
  // Weeks run to 30, not to 28: the month count divides by 30, so handing it
  // 28 days would say "0 months ago".
  if (days < 30) return plural(Math.floor(days / 7), 'week');
  if (days < 365) return plural(Math.floor(days / 30), 'month');
  return plural(Math.floor(days / 365), 'year');
}

/** Today → `success`, under a week → `info`, anything older → `warning`, all in calendar days. */
export function recencyTone(days: number): RecencyTone {
  if (days === 0) return 'success';
  if (days < RECENT_DAYS) return 'info';
  return 'warning';
}
